import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BASE_URL } from '../Config';
import keepaiLogo from '../resource/keepaiLogo.png';
import '../css/style/style.css';
import '../css/style/chat.css';

const getAuth = () => {
    const stored = localStorage.getItem('user');
    if (!stored) return null;
    return JSON.parse(stored);
};

const authHeader = () => {
    const user = getAuth();
    return user?.token ? { Authorization: `Bearer ${user.token}` } : {};
};

const Chat = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const initialQuery = location.state?.query || '';
    const initialSessionId = location.state?.sessionId || crypto.randomUUID();

    const [sessionId] = useState(initialSessionId);
    const [messages, setMessages] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [imageMode, setImageMode] = useState(false);
    const [imageLoading, setImageLoading] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [roleBlocked, setRoleBlocked] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
    const [pwMsg, setPwMsg] = useState(null);
    const [model, setModel] = useState(location.state?.model || 'chatgpt');
    const [subscriptions, setSubscriptions] = useState({ chatgpt: false, claude: false });
    const [showClaudeModal, setShowClaudeModal] = useState(false);
    const chatBodyRef = useRef(null);
    const textareaRef = useRef(null);
    const user = getAuth();

    // 관리자(R)는 유료(Y)와 동일하게 채팅 허용 + Claude 구독 없이 사용 가능
    const isPaid = user?.role === 'Y' || user?.role === 'R';
    const canUseClaude = subscriptions.claude || user?.role === 'R';

    const scrollToBottom = useCallback(() => {
        if (chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
        }
    }, []);

    const loadSessions = useCallback(async () => {
        if (!user?.token) return;
        try {
            const res = await fetch(`${BASE_URL}/chat/sessions`, { headers: authHeader() });
            const data = await res.json();
            setSessions(data.sessions || []);
        } catch {}
    }, []);

    const loadUserInfo = useCallback(async () => {
        if (!user?.token) return;
        try {
            const res = await fetch(`${BASE_URL}/user/me`, { headers: authHeader() });
            const data = await res.json();
            if (data.success) setUserInfo(data);
        } catch {}
    }, []);

    const loadSubscriptions = useCallback(async () => {
        if (!user?.token) return;
        try {
            const res = await fetch(`${BASE_URL}/user/subscriptions`, { headers: authHeader() });
            const data = await res.json();
            setSubscriptions(data);
        } catch {}
    }, []);

    const handlePasswordChange = async () => {
        if (!pwForm.next || pwForm.next !== pwForm.confirm) {
            setPwMsg({ ok: false, text: '새 비밀번호가 일치하지 않습니다.' });
            return;
        }
        try {
            const res = await fetch(`${BASE_URL}/user/password`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ currentPassword: pwForm.current, newPassword: pwForm.next }),
            });
            const data = await res.json();
            if (data.success) {
                setPwMsg({ ok: true, text: '비밀번호가 변경되었습니다.' });
                setPwForm({ current: '', next: '', confirm: '' });
            } else {
                setPwMsg({ ok: false, text: data.message || '변경 실패' });
            }
        } catch {
            setPwMsg({ ok: false, text: '서버 오류' });
        }
    };

    const loadHistory = useCallback(async (sid) => {
        if (!user?.token) return;
        try {
            const res = await fetch(`${BASE_URL}/chat/history/${sid}`, { headers: authHeader() });
            const data = await res.json();
            setMessages(data.messages || []);
        } catch {}
    }, []);

    useEffect(() => {
        if (user?.token) {
            loadSessions();
            loadUserInfo();
            loadSubscriptions();
            if (location.state?.sessionId) {
                loadHistory(location.state.sessionId);
            } else if (initialQuery && isPaid) {
                sendMessage(initialQuery);
            }
        }
    }, []); // eslint-disable-line

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    const sendMessage = async (text) => {
        const userText = text ?? input.trim();
        if (!userText || loading) return;

        const newMessages = [...messages, { role: 'user', content: userText }];
        setMessages(newMessages);
        setInput('');
        setLoading(true);
        if (textareaRef.current) textareaRef.current.style.height = 'auto';

        // 이미지 메시지는 OpenAI chat API에 포함하지 않음 (base64 payload 방지)
        const apiMessages = newMessages
            .filter(m => m.type !== 'image' && m.type !== 'image-loading')
            .map(m => ({ role: m.role, content: m.content }));

        const assistantIndex = newMessages.length;
        setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
        let fullResponse = '';
        let rafId = null;

        try {
            const res = await fetch(`${BASE_URL}/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ messages: apiMessages, sessionId, model }),
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                if (res.status === 403) {
                    setMessages(prev => {
                        const updated = [...prev];
                        updated[assistantIndex] = { role: 'assistant', content: '' };
                        return updated.slice(0, assistantIndex);
                    });
                    setShowClaudeModal(true);
                    setLoading(false);
                    return;
                }
                throw Object.assign(new Error(errData.error || '오류'), { status: res.status });
            }

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            const flushUpdate = () => {
                const snapshot = fullResponse;
                setMessages(prev => {
                    const updated = [...prev];
                    updated[assistantIndex] = { ...updated[assistantIndex], content: snapshot };
                    return updated;
                });
                rafId = null;
            };

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop();

                let hasNew = false;
                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    const data = line.slice(6).trim();
                    if (data === '[DONE]') break;
                    try {
                        const delta = JSON.parse(data).choices?.[0]?.delta?.content;
                        if (delta) {
                            fullResponse += delta;
                            hasNew = true;
                        }
                    } catch {}
                }

                // rAF로 묶어서 프레임당 1회만 re-render
                if (hasNew && !rafId) {
                    rafId = requestAnimationFrame(flushUpdate);
                }
            }

            // 남은 업데이트 강제 반영
            if (rafId) cancelAnimationFrame(rafId);
            if (fullResponse) {
                setMessages(prev => {
                    const updated = [...prev];
                    updated[assistantIndex] = { ...updated[assistantIndex], content: fullResponse };
                    return updated;
                });
            }
        } catch (err) {
            if (rafId) cancelAnimationFrame(rafId);
            const msg = err?.status === 429
                ? 'AI 서버가 혼잡합니다. 잠시 후 다시 시도해주세요.'
                : '오류가 발생했습니다. 다시 시도해주세요.';
            setMessages(prev => {
                const updated = [...prev];
                updated[assistantIndex] = { role: 'assistant', content: msg };
                return updated;
            });
        } finally {
            setLoading(false);
            loadSessions();
        }
    };

    const sendImage = async () => {
        if (!requireAuth()) return;
        const prompt = input.trim();
        if (!prompt || imageLoading || loading) return;

        const newMessages = [...messages, { role: 'user', content: prompt }];
        setMessages(newMessages);
        setInput('');
        setImageLoading(true);
        if (textareaRef.current) textareaRef.current.style.height = 'auto';

        const placeholderIdx = newMessages.length;
        setMessages(prev => [...prev, { role: 'assistant', content: '', type: 'image-loading' }]);

        try {
            const res = await fetch(`${BASE_URL}/image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ prompt, sessionId }),
            });
            const data = await res.json();
            if (data.success) {
                setMessages(prev => {
                    const updated = [...prev];
                    updated[placeholderIdx] = { role: 'assistant', content: data.image, type: 'image' };
                    return updated;
                });
            } else {
                setMessages(prev => {
                    const updated = [...prev];
                    updated[placeholderIdx] = { role: 'assistant', content: '이미지 생성에 실패했습니다.' };
                    return updated;
                });
            }
        } catch {
            setMessages(prev => {
                const updated = [...prev];
                updated[placeholderIdx] = { role: 'assistant', content: '오류가 발생했습니다. 다시 시도해주세요.' };
                return updated;
            });
        } finally {
            setImageLoading(false);
            loadSessions();
        }
    };

    const requireAuth = () => {
        if (!user?.token) { setShowAuthModal(true); return false; }
        if (!isPaid) { setRoleBlocked(true); return false; }
        return true;
    };

    const handleKeyDown = (e) => {
        if (!user?.token) { e.preventDefault(); setShowAuthModal(true); return; }
        if (!isPaid) { e.preventDefault(); setRoleBlocked(true); return; }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            imageMode ? sendImage() : sendMessage();
        }
    };

    const handleInput = (e) => {
        if (!requireAuth()) return;
        setInput(e.target.value);
        const el = textareaRef.current;
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 160) + 'px';
    };

    const handleSendClick = () => {
        if (!requireAuth()) return;
        imageMode ? sendImage() : sendMessage();
    };

    const startNewChat = () => {
        navigate('/chat', { state: { sessionId: crypto.randomUUID() } });
        window.location.reload();
    };

    const openSession = (sid) => {
        navigate('/chat', { state: { sessionId: sid } });
        window.location.reload();
    };

    return (
        <div className="chat-page">
            {/* 비회원 접근 차단 모달 */}
            {showAuthModal && (
                <div className="auth-modal-backdrop" onClick={() => setShowAuthModal(false)}>
                    <div className="auth-modal" onClick={e => e.stopPropagation()}>
                        <div className="auth-modal-icon">🔒</div>
                        <h3 className="auth-modal-title">회원 전용 서비스</h3>
                        <p className="auth-modal-desc">채팅 기능은 로그인 후 이용하실 수 있습니다.</p>
                        <div className="auth-modal-btns">
                            <button
                                className="auth-modal-btn-login"
                                onClick={() => navigate('/login', { state: { tab: 'login' } })}
                            >로그인</button>
                            <button
                                className="auth-modal-btn-register"
                                onClick={() => navigate('/login', { state: { tab: 'register' } })}
                            >회원가입</button>
                        </div>
                        <button className="auth-modal-close" onClick={() => setShowAuthModal(false)}>닫기</button>
                    </div>
                </div>
            )}

            {/* Claude Pro 구독 필요 모달 */}
            {showClaudeModal && (
                <div className="auth-modal-backdrop" onClick={() => setShowClaudeModal(false)}>
                    <div className="auth-modal" onClick={e => e.stopPropagation()}>
                        <div className="auth-modal-icon">🔮</div>
                        <h3 className="auth-modal-title">Claude Pro 구독이 필요합니다</h3>
                        <p className="auth-modal-desc">
                            Claude Sonnet을 사용하려면<br/>Claude Pro 구독이 필요합니다.<br/><br/>
                            결제 비용 : 19,000원
                        </p>
                        <div className="auth-modal-btns">
                            <button className="auth-modal-btn-login" onClick={() => navigate('/payment', { state: { product: 'claude' } })}>구독하기</button>
                        </div>
                        <button className="auth-modal-close" onClick={() => setShowClaudeModal(false)}>닫기</button>
                    </div>
                </div>
            )}

            {/* 이용 권한 없음 모달 */}
            {roleBlocked && (
                <div className="auth-modal-backdrop" onClick={() => setRoleBlocked(false)}>
                    <div className="auth-modal" onClick={e => e.stopPropagation()}>
                        <div className="auth-modal-icon">💳</div>
                        <h3 className="auth-modal-title">현재 유료 서비스 이용 권한이 없습니다.</h3>
                        <p className="auth-modal-desc">
                            결제 비용 : 19,000원<br/><br/>
                            <strong>※ 결제 및 이용 문의</strong><br/>
                            연락처 : 1566-2144<br/>
                        </p>
                        <div className="auth-modal-btns">
                            <button className="auth-modal-btn-login" onClick={() => navigate('/payment')}>결제하기</button>
                        </div>
                        <button className="auth-modal-close" onClick={() => setRoleBlocked(false)}>닫기</button>
                    </div>
                </div>
            )}

            {/* 사이드바 */}
            {sidebarOpen && (
                <>
                    <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
                    <aside className="chat-sidebar">
                        <div className="sidebar-header">
                            <span>대화 기록</span>
                            <button className="sidebar-new-btn" onClick={startNewChat}>+ 새 대화</button>
                        </div>
                        <ul className="session-list">
                            {sessions.length === 0 && <li className="session-empty">대화 기록이 없습니다</li>}
                            {sessions.map(s => (
                                <li
                                    key={s.SESSION_ID}
                                    className={`session-item ${s.SESSION_ID === sessionId ? 'active' : ''}`}
                                    onClick={() => openSession(s.SESSION_ID)}
                                >
                                    <span className="session-preview">{s.preview?.slice(0, 30) || '새 대화'}</span>
                                    <span className="session-date">{new Date(s.last_at).toLocaleDateString('ko-KR')}</span>
                                </li>
                            ))}
                        </ul>

                        {/* 설정 패널 */}
                        <div className="sidebar-settings">
                            <button
                                className="settings-toggle"
                                onClick={() => setSettingsOpen(v => !v)}
                            >
                                ⚙ 설정 {settingsOpen ? '▴' : '▾'}
                            </button>
                            {settingsOpen && userInfo && (
                                <div className="settings-panel">
                                    <div className="settings-info-row">
                                        <span className="settings-label">이름</span>
                                        <span className="settings-value">{userInfo.name}</span>
                                    </div>
                                    <div className="settings-info-row">
                                        <span className="settings-label">아이디</span>
                                        <span className="settings-value">{userInfo.userId}</span>
                                    </div>
                                    <div className="settings-info-row">
                                        <span className="settings-label">전화번호</span>
                                        <span className="settings-value">{userInfo.phone}</span>
                                    </div>

                                    <div className="settings-divider" />
                                    <p className="settings-section-title">비밀번호 변경</p>

                                    <input
                                        className="settings-input"
                                        type="password"
                                        placeholder="현재 비밀번호"
                                        value={pwForm.current}
                                        onChange={e => { setPwForm(p => ({ ...p, current: e.target.value })); setPwMsg(null); }}
                                    />
                                    <input
                                        className="settings-input"
                                        type="password"
                                        placeholder="새 비밀번호"
                                        value={pwForm.next}
                                        onChange={e => { setPwForm(p => ({ ...p, next: e.target.value })); setPwMsg(null); }}
                                    />
                                    <input
                                        className="settings-input"
                                        type="password"
                                        placeholder="새 비밀번호 확인"
                                        value={pwForm.confirm}
                                        onChange={e => { setPwForm(p => ({ ...p, confirm: e.target.value })); setPwMsg(null); }}
                                    />
                                    {pwMsg && (
                                        <p className={`settings-msg ${pwMsg.ok ? 'ok' : 'err'}`}>{pwMsg.text}</p>
                                    )}
                                    <button className="settings-save-btn" onClick={handlePasswordChange}>변경하기</button>
                                </div>
                            )}
                        </div>
                    </aside>
                </>
            )}

            {/* 상단 바 */}
            <header className="chat-header">
                <div className="chat-header-left">
                    {user?.token && (
                        <button className="sidebar-toggle" onClick={() => setSidebarOpen(v => !v)}>☰</button>
                    )}
                    <img src={keepaiLogo} alt="KeepAi" className="chat-logo" onClick={() => navigate('/')} />
                </div>
                <span className="chat-model-badge">{model === 'claude' ? 'Claude Sonnet' : 'GPT-5.5'}</span>
            </header>

            {/* 메시지 영역 */}
            <div className="chat-body" ref={chatBodyRef}>
                <div className="chat-messages-inner">
                    {messages.length === 0 && (
                        <div className="chat-empty">
                            <div className="chat-empty-icon">💬</div>
                            <p>무엇이든 물어보세요</p>
                        </div>
                    )}
                    {messages.map((msg, i) => (
                        <div key={i} className={`chat-message ${msg.role}`}>
                            {msg.role === 'assistant' && <div className="chat-avatar">K</div>}
                            <div className="chat-bubble">
                                {msg.type === 'image' ? (
                                    <div className="generated-image-wrap">
                                        <img src={msg.content} alt="생성된 이미지" className="generated-image" />
                                        <a
                                            href={msg.content}
                                            download="keepai-image.png"
                                            className="image-download-btn"
                                        >⬇ 다운로드</a>
                                    </div>
                                ) : msg.type === 'image-loading' ? (
                                    <span className="image-loading-text">🖼&nbsp; 이미지 생성 중... (최대 30초)</span>
                                ) : (
                                    msg.content || (loading && i === messages.length - 1
                                        ? <span className="typing-dot"><span /><span /><span /></span>
                                        : ''
                                    )
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* 입력 영역 */}
            <div className="chat-input-wrap">
                <div className="chat-mode-bar">
                    <button
                        className={`chat-mode-btn ${!imageMode ? 'active' : ''}`}
                        onClick={() => setImageMode(false)}
                    >💬 채팅</button>
                    <button
                        className={`chat-mode-btn ${imageMode ? 'active' : ''}`}
                        onClick={() => setImageMode(true)}
                    >🖼 이미지 생성</button>
                    {!imageMode && user?.token && (
                        <div className="chat-model-select">
                            <button
                                className={`model-btn ${model === 'chatgpt' ? 'active' : ''}`}
                                onClick={() => setModel('chatgpt')}
                            >GPT-5.5</button>
                            <button
                                className={`model-btn claude-btn ${model === 'claude' ? 'active' : ''} ${!canUseClaude ? 'locked' : ''}`}
                                onClick={() => {
                                    if (!canUseClaude) { setShowClaudeModal(true); }
                                    else { setModel('claude'); }
                                }}
                            >{canUseClaude ? 'Claude Pro' : '🔒 Claude Pro'}</button>
                        </div>
                    )}
                </div>
                <div className="chat-input-box">
                    <textarea
                        ref={textareaRef}
                        rows={1}
                        value={input}
                        onChange={handleInput}
                        onKeyDown={handleKeyDown}
                        placeholder={imageMode
                            ? '생성할 이미지를 설명하세요... (예: 석양이 지는 해변의 고양이)'
                            : '메시지를 입력하세요... (Enter 전송, Shift+Enter 줄바꿈)'}
                        disabled={loading || imageLoading}
                    />
                    <button
                        className={`chat-send-btn ${imageMode ? 'image' : ''}`}
                        onClick={handleSendClick}
                        disabled={loading || imageLoading || (!user?.token ? false : !input.trim())}
                    >{imageMode ? '🖼' : '↑'}</button>
                </div>
                <p className="chat-disclaimer">AI가 생성한 내용은 사실과 다를 수 있습니다.</p>
            </div>
        </div>
    );
};

export default Chat;
